import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, message } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleFilled, HeartFilled, UserOutlined } from '@ant-design/icons';
import { useDoctorQuery } from '../../../hooks/useDoctorQuery';
import { useLanguage } from '../../../context/LanguageContext';
import drEasyApi from '../../../Api/Customapi';
import { BiClinic } from 'react-icons/bi';
import { slug } from '../../../utils/store';
import NeedAuthModal from '../../Modals/NeedAuthModal';
import LoginModal from '../../Modals/LoginModal';
import { useGetCenterById } from '../../../hooks/useGetCenterById';

const QUICK_TAGS = [
  'review.tags.excellentService',
  'review.tags.punctual',
  'review.tags.clearExplanation',
  'review.tags.cooperative',
  'review.tags.cleanEnvironment',
  'review.tags.quickDiagnosis',
];

const PublicReview = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const isRTL = currentLanguage === 'ar';
  const { centerId } = useParams();
  const { data: centerData } = useGetCenterById(centerId);
  const { data: doctor } = useDoctorQuery(centerData?.slug);

  const clinics = doctor?.clinics || [];
  const ratingRef = useRef(null);

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userName, setUserName] = useState('');
  const [nameError, setNameError] = useState('');

  const isAuthorized = !!localStorage.getItem(`userTkn-${slug}`) || !!sessionStorage.getItem(`userTkn-${slug}`);
  const [loginModal, setLoginModal] = useState(false);

  const getRatingLabel = (val) => {
    const labels = {
      1: t('review.ratings.poor'),
      2: t('review.ratings.fair'),
      3: t('review.ratings.good'),
      4: t('review.ratings.veryGood'),
      5: t('review.ratings.excellent'),
    };
    return labels[val] || '';
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleNameChange = (e) => {
    setUserName(e.target.value);
    if (e.target.value.trim()) {
      setNameError('');
    }
  };

  const handleSubmit = async () => {
    if (!userName.trim()) {
      setNameError(t('review.pleaseEnterName') || 'Please enter your name');
      return;
    }
    if (rating === 0) {
      message.warning(t('review.pleaseSelectRating'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        text: reviewText,
        rating,
        name: userName,
        photoId: ' ',
        centerId: centerData?.id,
        ...(selectedClinic && { clinicId: selectedClinic.clinicId }),
      };
      await drEasyApi.post('/api/Review/PublicReview', payload);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (error) {
      message.error(error.response?.data?.message || t('review.failedToSubmit'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Success Screen ───────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen max-w-7xl bg-cardColor flex flex-col mx-auto items-center justify-center px-6 text-center gap-5">
        <div className="w-20 h-20 rounded-full bg-primary/10  flex items-center justify-center">
          <HeartFilled className="text-red-600 text-4xl animate-pulse" />
        </div>
        <h2 className="text-xl font-medium text-primary">
          {t('review.successTitle')}
        </h2>
        <p className="text-sm text-gray-500 max-w-xs leading-relaxed">
          {t('review.successMessage')}
        </p>
        <button
          onClick={() => navigate("/home")}
          className="mt-2 px-6 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white hover:bg-gray-50 transition"
        >
          {t('review.backToHome')}
        </button>
      </div>
    );
  }

  // ─── Main Screen ──────────────────────────────────────────────
  return (
    <div
      className={`min-h-screen max-w-7xl mx-auto bg-gray-50 flex flex-col ${isRTL ? 'text-right' : 'text-left'} justify-center mt-6`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-100 px-5 py-3.5 flex items-center gap-4 sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition"
        >
          {isRTL ? <ArrowRightOutlined style={{ fontSize: 15 }} /> : <ArrowLeftOutlined style={{ fontSize: 15 }} />}
        </button>
        <span className="text-base font-medium text-gray-900">
          {t('review.rateClinic')} {centerData?.name }
        </span>
      </div>

      <div className="flex-1 px-5 py-6 flex flex-col gap-5 overflow-y-auto">

        {/* ── Name Field Card ── */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
            {t('review.yourName') || 'Your Name'}
            <span className="text-red-400 ml-0.5">*</span>
          </p>

          <div className="relative flex items-center">
            <div className="absolute left-3 w-9 h-9 rounded-full bg-primary/20  flex items-center justify-center pointer-events-none flex-shrink-0">
              <UserOutlined className="text-primary" />
            </div>
            <input
              type="text"
              value={userName}
              onChange={handleNameChange}
              onBlur={() => {
                if (!userName.trim()) {
                  setNameError(t('review.pleaseEnterName') || 'Please enter your name');
                }
              }}
              placeholder={t('review.enterYourName') || 'Enter your name'}
              className={`w-full h-12 pl-14 pr-4 border-[1.5px] rounded-xl text-sm bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white transition ${
                nameError
                  ? 'border-red-400 focus:border-red-400 focus:ring-2 focus:ring-red-100'
                  : 'border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100'
              }`}
            />
          </div>

          {/* Inline error */}
          {nameError && (
            <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5.5" stroke="#ef4444" />
                <path d="M6 3.5v3M6 8h.01" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
              {nameError}
            </p>
          )}

       
        </div>

        {/* Clinic Picker */}
        {/* <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-3">
            {t('review.selectClinic')}
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            {clinics.map((clinic) => {
              const isSelected = selectedClinic?.clinicId === clinic?.clinicId;
              return (
                <button
                  key={clinic?.clinicId}
                  onClick={() => {
                    setSelectedClinic(clinic);
                    setRating(0);
                    setReviewText('');
                    setSelectedTags([]);
                    setTimeout(() => {
                      ratingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 50);
                  }}
                  className={`w-full flex items-center gap-3 bg-cardColor px-4 py-3 rounded-lg border transition text-start ${
                    isSelected
                      ? 'border-secondary bg-cardColor'
                      : 'border-gray-200 bg-cardColor/80 hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-primary/20' : 'bg-gray-100'
                    }`}
                  >
                    <BiClinic
                      className={`text-lg ${isSelected ? 'text-primary' : 'text-gray-500'}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        isSelected ? 'goldy' : 'text-gray-800'
                      }`}
                    >
                      {clinic?.clinicName}
                    </p>
                    {clinic?.detailedAddress && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {clinic?.detailedAddress}
                      </p>
                    )}
                  </div>
                  {isSelected && (
                    <div className="w-4 h-4 rounded-full bg-goldy flex items-center justify-center flex-shrink-0">
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <polyline points="1,4 3,6 7,2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div> */}

        {/* Rating */}
        <div ref={ratingRef} className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 text-center mb-4">
            {t('review.howWouldYouRate')}
          </p>
          <div className="flex justify-center gap-2.5 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="text-4xl leading-none transition-transform hover:scale-110 active:scale-95"
                style={{
                  color: star <= (hoveredRating || rating) ? '#F5C400' : '#D1D5DB',
                }}
              >
                ★
              </button>
            ))}
          </div>
          {rating > 0 && (
            <div className="flex justify-center mb-4">
              <span className="text-sm font-medium text-amber-800 bg-amber-50 px-4 py-1 rounded-full">
                {getRatingLabel(rating)}
              </span>
            </div>
          )}
        </div>

        {/* Review Text */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-sm font-medium text-gray-500 mb-3">
            {t('review.shareYourExperience')}
          </p>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder={`${t('review.tellOthersCenter', { name: selectedClinic?.clinicName })}...`}
            maxLength={500}
            rows={5}
            dir={isRTL ? 'rtl' : 'ltr'}
            className="w-full border border-gray-200 rounded-lg px-3.5 py-3 text-sm text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:border-secondary transition"
          />
          <p className="text-xs text-gray-400 text-end mt-1.5">
            {reviewText.length} / 500
          </p>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || !userName.trim() || loading}
          className={`w-full py-3.5 rounded-xl text-white text-base font-medium transition ${
            rating === 0 || !userName.trim() || loading
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-goldy hover:bg-goldy active:scale-[0.98]'
          }`}
        >
          {t('review.submitReview')}
        </button>
      </div>
    </div>
  );
};

export default PublicReview;